import { useEffect, useState } from "react";
import { BellRing, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const eur = (v) => (v == null ? "—" : new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(v));
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" }) : "");

export default function BudgetBar({ budget, onSaved }) {
  const [value, setValue] = useState(budget?.monthly_eur ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(budget?.monthly_eur ?? ""); }, [budget?.monthly_eur]);
  if (!budget) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/help/budget", { monthly_eur: value === "" ? null : Number(value) });
      toast.success(data.monthly_eur ? `Maandbudget ingesteld op ${eur(data.monthly_eur)}.` : "Maandbudget uitgeschakeld.");
      onSaved?.(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  };

  const pct = budget.pct == null ? null : Math.min(budget.pct, 100);
  const tone = budget.exceeded ? "bg-destructive" : budget.pct >= 80 ? "bg-secondary" : "bg-primary";
  return (
    <div className="border-2 border-foreground bg-background p-3" data-testid="help-budget">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <form onSubmit={save} className="flex items-center gap-2 flex-wrap">
          <label className="font-display uppercase text-[10px] tracking-widest">Maandbudget</label>
          <div className="flex items-center border-2 border-foreground bg-background">
            <span className="px-2 text-sm text-muted-foreground">€</span>
            <input type="number" min="0" step="1" value={value} onChange={(e) => setValue(e.target.value)} placeholder="geen" data-testid="help-budget-input"
              className="w-24 py-1 pr-2 text-sm font-mono-num bg-transparent focus:outline-none" />
          </div>
          <button type="submit" disabled={saving} data-testid="help-budget-save"
            className="btn-brutal bg-foreground text-background px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Opslaan
          </button>
        </form>
        <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
          <BellRing className="w-3.5 h-3.5" /> Bij 100% één mail per maand naar <span className="font-mono-num">{budget.notify_to || "— (ADMIN_NOTIFY_EMAIL ontbreekt)"}</span>; de chat blijft werken.
        </div>
      </div>
      {budget.monthly_eur ? (
        <div className="mt-3" data-testid="help-budget-progress">
          <div className="flex justify-between text-[11px] mb-1">
            <span>{budget.month_label}: <b className="font-mono-num">{eur(budget.spent_eur)}</b> van {eur(budget.monthly_eur)}</span>
            <span className={`font-mono-num font-bold ${budget.exceeded ? "text-destructive" : ""}`} data-testid="help-budget-pct">{budget.pct}%</span>
          </div>
          <div className="h-3 border-2 border-foreground bg-muted/40 overflow-hidden">
            <div className={`h-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
          </div>
          {budget.exceeded && (
            <div className="mt-2 inline-flex items-center gap-1.5 bg-destructive text-white px-2 py-1 text-[11px] font-display uppercase tracking-widest" data-testid="help-budget-exceeded">
              <BellRing className="w-3 h-3" /> Budget bereikt{budget.alert_sent_at ? ` · mail verstuurd ${fmt(budget.alert_sent_at)}` : ""}
            </div>
          )}
        </div>
      ) : <p className="text-[11px] text-muted-foreground mt-2" data-testid="help-budget-none">Geen budget ingesteld — vul een bedrag in om een alarm te ontvangen.</p>}
    </div>
  );
}
